import { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandMore, InfoOutlined, Refresh } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { dataProvider } from "../../providers/dataProvider";

export type DriftCell = { text: string; href: string | null };
export type DriftCheck = {
  name: string;
  label: string;
  help?: { trigger: string; fix: string[] };
  count: number;
  table?: { headers: string[]; rows: DriftCell[][] };
};
export type DriftRun = {
  id: number;
  ran_at: string;
  triggered_by: string;
  checks: DriftCheck[];
};

// Org links stay inside admin; Xero links open Xero in a new tab.
const Cell = ({ cell }: { cell: DriftCell }) =>
  !cell.href ? (
    <>{cell.text}</>
  ) : cell.href.startsWith("/") ? (
    <Link component={RouterLink} to={cell.href}>
      {cell.text}
    </Link>
  ) : (
    <Link href={cell.href} target="_blank" rel="noopener noreferrer">
      {cell.text}
    </Link>
  );

const HelpIcon = ({ help }: { help: NonNullable<DriftCheck["help"]> }) => (
  <Tooltip
    arrow
    componentsProps={{ tooltip: { sx: { maxWidth: 560 } } }}
    title={
      <Box p={0.5}>
        <Typography variant="subtitle2">Why this shows</Typography>
        <Typography variant="body2" mb={1}>
          {help.trigger}
        </Typography>
        <Typography variant="subtitle2">How to fix it</Typography>
        <ol style={{ margin: 0, paddingLeft: 18 }}>
          {help.fix.map((step) => (
            <li key={step}>
              <Typography variant="body2">{step}</Typography>
            </li>
          ))}
        </ol>
      </Box>
    }
  >
    {/* Inside the accordion header: a click here must not toggle the section. */}
    <IconButton
      size="small"
      aria-label="About this check"
      onClick={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()}
      sx={{ mx: 0.5 }}
    >
      <InfoOutlined fontSize="small" />
    </IconButton>
  </Tooltip>
);

const BillingDriftReport = () => {
  const [run, setRun] = useState<DriftRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await dataProvider.custom!({
        url: "/xero/billing-drift",
        method: "get",
      });
      setRun(result.data as unknown as DriftRun | null);
      setError(null);
    } catch {
      setError("Could not load the billing drift report.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rerun = async () => {
    setRunning(true);
    try {
      const result = await dataProvider.custom!({
        url: "/xero/billing-drift/run",
        method: "post",
        payload: {},
      });
      setRun(result.data as unknown as DriftRun);
      setError(null);
    } catch {
      setError("The re-run failed. Try again or check the API logs.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Typography variant="h5" flexGrow={1}>
          Billing Drift Report
        </Typography>
        <Button
          variant="contained"
          startIcon={running ? <CircularProgress size={16} /> : <Refresh />}
          onClick={rerun}
          disabled={running}
        >
          Re-run drift report
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <CircularProgress />
      ) : !run ? (
        !error && (
          <Alert severity="info">
            No run yet. The report runs daily, or use the button to run it now.
          </Alert>
        )
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Last run {new Date(run.ran_at).toLocaleString()} ({run.triggered_by}
            ).
          </Typography>
          {run.checks.map((check) => (
            <Accordion key={check.name}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography alignSelf="center">{check.label}</Typography>
                {check.help && <HelpIcon help={check.help} />}
                <Box flexGrow={1} />
                <Chip
                  label={check.count}
                  color={check.count ? "warning" : "success"}
                  size="small"
                />
              </AccordionSummary>
              <AccordionDetails sx={{ overflowX: "auto" }}>
                {check.count === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nothing found.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {(check.table?.headers ?? []).map((h, i) => (
                          <TableCell key={i}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(check.table?.rows ?? []).map((row, i) => (
                        <TableRow key={i}>
                          {row.map((cell, j) => (
                            <TableCell key={j}>
                              <Cell cell={cell} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </Box>
  );
};

export default BillingDriftReport;
